import { articles } from "@/mocks/articles";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import { articleContents } from "@/mocks/articleContents";

export type Article = (typeof articles)[number];
export type Topic = (typeof topics)[number];
export type Category = (typeof categories)[number];
export type ContentBlock = { type: string; text: string };

export function getCategory(slug: string): Category | undefined {
  return categories.find((c) => c.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategory(slug)?.name ?? "资讯";
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((a) => a.id === id);
}

export function getContentById(id: string): ContentBlock[] {
  const map = articleContents as Record<string, ContentBlock[]>;
  return map[id] ?? [];
}

export function getArticlesByCategory(slug: string): Article[] {
  return articles.filter((a) => a.category === slug);
}

export function getFeaturedArticles(): Article[] {
  return articles.filter((a) => a.featured);
}

export function getHotArticles(): Article[] {
  return [...articles].sort((a, b) => b.views - a.views);
}

export function getLatestArticles(): Article[] {
  return [...articles].sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function getTopicById(id: string): Topic | undefined {
  return topics.find((t) => t.id === id);
}

export function getTopicArticles(topic: Topic | undefined): Article[] {
  if (!topic) return [];
  return topic.articleIds
    .map((id) => getArticleById(id))
    .filter((a): a is Article => Boolean(a));
}

export function getRelatedTopics(topic: Topic | undefined): Topic[] {
  if (!topic) return [];
  return topic.relatedTopicIds
    .map((id) => getTopicById(id))
    .filter((t): t is Topic => Boolean(t));
}

export function getRelatedArticles(article: Article, limit = 3): Article[] {
  const sameCat = articles.filter((a) => a.id !== article.id && a.category === article.category);
  const sameTag = articles.filter(
    (a) => a.id !== article.id && a.category !== article.category && a.tags.some((t) => article.tags.includes(t)),
  );
  return [...sameCat, ...sameTag].slice(0, limit);
}

export function searchArticles(keyword: string): Article[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return articles.filter((a) => {
    const haystack = [a.title, a.summary, a.author, ...a.tags, getCategoryName(a.category)]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function formatDate(date: string): string {
  const [y, m, d] = date.split("-");
  if (!y || !m || !d) return date;
  return `${y}年${m}月${d}日`;
}

export function formatDateShort(date: string): string {
  const [, m, d] = date.split("-");
  if (!m || !d) return date;
  return `${m}.${d}`;
}